import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, type DashboardData } from '../../api/admin';
import { fmtTime } from '../../lib/format';
import { statusUi, ago } from '../../lib/statusUi';
import type { ReadingStatus } from '../../lib/schedule';
import { groupBy } from '../../lib/pmsGroup';
import { useAsync, Banner, Item, Empty } from './ui';

const today = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { data, error, loading, reload } = useAsync(() => adminApi.dashboard(), [], 'adminDashboard');
  const nav = useNavigate();
  const [eqFilter, setEqFilter] = useState<'all' | 'due' | 'overdue'>('all');
  const [catFilter, setCatFilter] = useState<'daily' | 'pms'>('daily');
  const listRef = useRef<HTMLHeadingElement>(null);

  function focusList(f: 'due' | 'overdue') {
    setEqFilter((cur) => (cur === f ? 'all' : f));
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30);
  }

  const dailyCount = data?.equipment.filter((e) => (e.category ?? 'daily') === 'daily').length ?? 0;
  const pmsCount = data?.equipment.filter((e) => e.category === 'pms').length ?? 0;

  const shownEquip = useMemo(() => {
    const list = (data?.equipment ?? []).filter((e) => (e.category ?? 'daily') === catFilter);
    if (eqFilter === 'due') return list.filter((e) => e.status === 'DUE' || e.status === 'NO_HISTORY');
    if (eqFilter === 'overdue') return list.filter((e) => e.status === 'OVERDUE');
    return list;
  }, [data, eqFilter, catFilter]);
  const shownGroups = useMemo(
    () => (catFilter === 'pms' ? groupBy(shownEquip, (e) => e.pmsGroup || 'Other') : []),
    [shownEquip, catFilter],
  );

  return (
    <div>
      <div className="spread">
        <h1>Dashboard</h1>
        <button className="secondary sm" onClick={reload}>Refresh</button>
      </div>
      <Banner error={error} />
      {loading && <div className="spinner" />}
      {data && (
        <>
          <div className="statRow">
            <Stat n={data.totals.equipment} label="Equipment" onClick={() => nav('/admin/equipment')} />
            <Stat n={data.totals.readingsToday} label="Readings today"
              onClick={() => nav(`/admin/readings?from=${today()}&to=${today()}`)} />
            <Stat n={data.totals.due} label="Due now" tone={data.totals.due ? 'warn' : undefined}
              active={eqFilter === 'due'} onClick={() => focusList('due')} />
            <Stat n={data.totals.overdue} label="Overdue" tone={data.totals.overdue ? 'bad' : undefined}
              active={eqFilter === 'overdue'} onClick={() => focusList('overdue')} />
            <Stat n={data.totals.abnormalToday} label="Abnormal today" tone={data.totals.abnormalToday ? 'warn' : undefined}
              onClick={() => nav(`/admin/readings?from=${today()}&to=${today()}&status=abnormal`)} />
          </div>

          <div className="spread">
            <h2 ref={listRef}>
              Equipment status
              {eqFilter !== 'all' && <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}> — {eqFilter}</span>}
            </h2>
            {eqFilter !== 'all' && <button className="ghost sm" onClick={() => setEqFilter('all')}>show all</button>}
          </div>
          <div className="choice-group" role="tablist" aria-label="Equipment view" style={{ marginBottom: 12 }}>
            <button type="button" className={`choice-btn ${catFilter === 'daily' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'daily'} onClick={() => setCatFilter('daily')}>
              Daily readings ({dailyCount})
            </button>
            <button type="button" className={`choice-btn ${catFilter === 'pms' ? 'on' : ''}`} role="tab" aria-selected={catFilter === 'pms'} onClick={() => setCatFilter('pms')}>
              PMS checks ({pmsCount})
            </button>
          </div>

          {shownEquip.length === 0 && <Empty icon="🔌">{eqFilter === 'all' ? 'No active equipment here.' : `Nothing ${eqFilter} right now.`}</Empty>}

          {catFilter === 'daily' && (
            <div className="cardlist">
              {shownEquip.map((e) => <EquipItem key={e.equipmentId} e={e} />)}
            </div>
          )}
          {catFilter === 'pms' && shownGroups.map((g) => (
            <div key={g.label} style={{ marginBottom: 4 }}>
              <h3 className="pmsGroup-head">{g.label} <span className="muted">({g.items.length})</span></h3>
              <div className="cardlist">
                {g.items.map((e) => <EquipItem key={e.equipmentId} e={e} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function EquipItem({ e }: { e: DashboardData['equipment'][number] }) {
  const ui = statusUi(e.status as ReadingStatus);
  return (
    <Item
      stripe={ui.tone === 'muted' ? undefined : ui.tone}
      title={e.name}
      meta={<>
        <span>{e.location}</span>
        <span>Last {ago(e.lastReadingAt)}</span>
        <span>Next {fmtTime(e.nextRecommendedAt)}</span>
      </>}
      actions={<span className={`chip ${chip(ui.tone)}`}>{ui.label}</span>}
    />
  );
}

function Stat({ n, label, tone, onClick, active }: { n: number; label: string; tone?: 'bad' | 'warn'; onClick?: () => void; active?: boolean }) {
  return (
    <button type="button" className={`stat statBtn ${tone ?? ''} ${active ? 'on' : ''}`} onClick={onClick}>
      <div className="statN">{n}</div>
      <div className="statL">{label} <span className="statGo">›</span></div>
    </button>
  );
}
function chip(t: string) { return t === 'bad' ? 'bad' : t === 'warn' ? 'warn' : t === 'ok' ? 'ok' : ''; }
